/**
 * The 7-bag randomiser.
 *
 * A bag is the seven letters in a uniformly random order; the queue is a
 * concatenation of whole bags. That guarantees the classic property — you never
 * wait more than twelve pieces for any letter — and it is reproducible from the
 * seed alone, because the only randomness is the engine's PRNG.
 *
 * The refill threshold is a pure function of the config, never of what a
 * renderer happens to ask for. A preview that pulled from the RNG would make
 * drawing the next-queue change the game.
 */
import { nextInt } from './rng.js';
import { PIECE_KINDS } from './pieces.js';
import type { EngineConfig, GameState, PieceKind, RngState } from './types.js';

/** One bag: all seven letters, uniformly shuffled. */
export function shuffledBag(rng: RngState): PieceKind[] {
  const source = [...PIECE_KINDS];
  const bag: PieceKind[] = [];
  // Selection shuffle: repeatedly take a uniformly random remaining letter.
  while (source.length > 0) {
    bag.push(...source.splice(nextInt(rng, source.length), 1));
  }
  return bag;
}

/**
 * How long the queue is kept.
 *
 * A whole bag beyond the deepest preview, so `peekNext` can always be answered
 * from what is already drawn and never has to touch the RNG.
 */
export function queueTarget(config: EngineConfig): number {
  return PIECE_KINDS.length + Math.max(0, config.nextPreview);
}

/** Top the queue up with whole bags until it is at least `queueTarget` long. */
export function refillQueue(state: GameState): void {
  const target = queueTarget(state.config);
  while (state.queue.length < target) {
    state.queue.push(...shuffledBag(state.rng));
  }
}

/** Take the next piece, then top the queue back up. */
export function drawNext(state: GameState): PieceKind {
  refillQueue(state);
  const drawn = state.queue.splice(0, 1);
  refillQueue(state);
  // `queueTarget` is at least seven, so the splice above always yielded one.
  return drawn[0] ?? 'I';
}

/**
 * The next `count` pieces, without drawing them.
 *
 * Pure: it neither advances the RNG nor changes the queue, so a renderer
 * previewing more or fewer pieces cannot change the game.
 */
export function peekNext(state: GameState, count: number): readonly PieceKind[] {
  return state.queue.slice(0, Math.max(0, count));
}
